import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormControlLabel, Paper, Switch, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  flipInterruttoreGate,
  getStatoInterruttoreGate,
} from '../../../../utils/api/iot_gate_api';

const useStyles = makeStyles((theme) => ({
  sezione: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    margin: '10px 0',
    padding: theme.spacing(3),
    textAlign: 'left',
  },
  descrizione: {
    color: theme.palette.text.secondary,
  },
  esito: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  errore: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  effetti: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    '& li': { lineHeight: 1.7 },
  },
}));

// Copy in the user's words, on purpose (review of PR 149): whoever reads this
// is a consulente or an esercente, and «ADR-0014», «gate nuovo», «catena Fase 0»
// mean nothing to them. The sentence says what changes for the esercizio when
// the switch is on, and names the module chip only to tell the two apart.
const DESCRIZIONE =
  'Diverso dalla sezione IoT dell’app (chip «IoT» qui sopra). Se attivo, per questo esercizio la Registrazione viene compilata automaticamente con la temperatura media dell’ultima ora rilevata dai sensori.';

const LETTURA_FALLITA =
  'Non è stato possibile leggere lo stato della registrazione automatica di questo esercizio.';

const FLIP_FALLITO =
  'Non è stato possibile cambiare lo stato della registrazione automatica di questo esercizio.';

// The four effects the DTO exposes separately, each named by what the
// esercizio sees happen. `ritiroCatenaFase0` is deliberately not listed: it is
// an internal decommissioning date, not operational information for this
// screen.
const descriviEffetti = (stato) => [
  `Valore registrato: ${
    stato.valoreScrittoSullaRegistrazione === 'MEDIA'
      ? 'temperatura media dell’ultima ora'
      : 'temperatura rilevata al momento della registrazione'
  }`,
  `Notifiche di anomalia: ${stato.notificheAttive ? 'attive' : 'spente'}`,
  `Segnalazione ai responsabili in caso di mancata presa in carico: ${
    stato.escalationAttiva ? 'attiva' : 'spenta'
  }`,
  `Riepilogo via email: ${stato.digestAttivo ? 'attivo' : 'spento'}`,
];

const etichettaStato = (attivo) =>
  attivo ? 'Attivo per questo esercizio' : 'Non attivo per questo esercizio';

/**
 * The per-esercizio switch of the IoT gate cutover (ADR-0014). What is on the
 * switch is what the server answered, never a local copy: the read follows the
 * mount and every flip, success or refusal alike, so the state on screen is the
 * one the gate is actually in — also when the PUT was refused (403 for whoever
 * is not admin) or when another admin flipped it in the meantime.
 *
 * Who flipped and when is the server's audit trail, not this screen's business.
 */
const InterruttoreGateIotSection = ({ esercizioId }) => {
  const classes = useStyles();
  const [stato, setStato] = useState(null);
  const [erroreLettura, setErroreLettura] = useState(null);
  const [esitoFlip, setEsitoFlip] = useState(null);
  const [erroreFlip, setErroreFlip] = useState(null);
  const [flipInCorso, setFlipInCorso] = useState(false);

  const caricaStato = useCallback(() => {
    if (esercizioId == null || esercizioId === '') {
      return Promise.resolve();
    }
    return getStatoInterruttoreGate(esercizioId)
      .then((letto) => {
        setStato(letto);
        setErroreLettura(null);
      })
      .catch((errore) => {
        // The old copy goes away with the error: a stale switch would be read
        // as the current position of the gate.
        setStato(null);
        setErroreLettura(
          `${LETTURA_FALLITA}${errore?.message ? ` ${errore.message}` : ''}`,
        );
      });
  }, [esercizioId]);

  useEffect(() => {
    caricaStato();
  }, [caricaStato]);

  const handleFlip = () => {
    if (!stato || flipInCorso) {
      return;
    }
    const richiesto = !stato.iotGateAttivo;
    setFlipInCorso(true);
    setEsitoFlip(null);
    setErroreFlip(null);
    flipInterruttoreGate(esercizioId, richiesto)
      .then((aggiornato) => {
        setEsitoFlip(etichettaStato(aggiornato.iotGateAttivo));
      })
      .catch((errore) => {
        setErroreFlip(errore?.message || FLIP_FALLITO);
      })
      // Re-read in both branches: the refusal case is the one where the switch
      // must snap back to what the server holds.
      .then(caricaStato)
      .finally(() => setFlipInCorso(false));
  };

  return (
    <Paper className={classes.sezione} data-testid="interruttore-gate-iot">
      <Typography variant="h6" component="h3">
        Registrazione automatica dai sensori IoT
      </Typography>
      <Typography variant="body2" component="p" className={classes.descrizione}>
        {DESCRIZIONE}
      </Typography>
      {stato && (
        <FormControlLabel
          control={
            <Switch
              checked={stato.iotGateAttivo}
              onChange={handleFlip}
              disabled={flipInCorso}
              color="primary"
              inputProps={{
                'aria-label': 'Registrazione automatica dai sensori IoT',
              }}
            />
          }
          label={etichettaStato(stato.iotGateAttivo)}
        />
      )}
      {stato && (
        <ul className={classes.effetti} data-testid="effetti-gate">
          {descriviEffetti(stato).map((effetto) => (
            <li key={effetto}>
              <Typography variant="body2" component="span">
                {effetto}
              </Typography>
            </li>
          ))}
        </ul>
      )}
      {erroreLettura && (
        <Typography
          variant="body2"
          component="p"
          role="alert"
          className={classes.errore}
        >
          {erroreLettura}
        </Typography>
      )}
      {esitoFlip && (
        <Typography
          variant="body2"
          component="p"
          role="status"
          className={classes.esito}
        >
          {esitoFlip}
        </Typography>
      )}
      {erroreFlip && (
        <Typography
          variant="body2"
          component="p"
          role="alert"
          className={classes.errore}
        >
          {erroreFlip}
        </Typography>
      )}
    </Paper>
  );
};

InterruttoreGateIotSection.propTypes = {
  esercizioId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default InterruttoreGateIotSection;
